import uuidv4 from 'uuid/v4';
import {
    updateConversationMessage,
    updateRecentChatMessage,
    updateMessageUnreadCount,
} from '../Components/WebChat/Common/createMessage';
import { getFormatPhoneNumber, sendNotification } from '../Helpers/Utility';
import {
    GROUP_CHAT_PROFILE_UPDATED_NOTIFY,
    MSG_SEEN_ACKNOWLEDGE_STATUS,
    MSG_SEEN_STATUS,
    MSG_SENT_SEEN_STATUS_CARBON,
    MSG_DELETE_STATUS,
    MSG_DELETE_STATUS_CARBON,
    MSG_DELIVERED_STATUS_CARBON,
    MSG_DELIVERED_STATUS,
    MSG_DELIVERED_STATUS_ID,
    MSG_SEEN_STATUS_ID,
    MSG_SEEN_STATUS_CARBON,
} from '../Helpers/Chat/Constant';
import { formatUserIdToJid, getContactNameFromRoster, getDataFromRoster } from '../Helpers/Chat/User';
import {
    getMutedChats,
    getPushNotificationData,
    handleArchiveActions,
    handleTempArchivedChats,
    getMessageFromHistoryById,
    isActiveConversationUserOrGroup,
    transformReactionsGrouped,
    updateReactionList,
} from '../Helpers/Chat/ChatHelper';
import { TYPE_DELAY_TIME } from '../Helpers/Constants';
import SDK from '../Components/SDK';
import messagesIdb from '../Helpers/MessagesIdb';
import logger from '../Helpers/logger';
import { RecentChatUpdateAction } from './RecentChatActions';
import { TypingDataAction, TypingDataRemove } from './TypingAction';
import { deleteChatSeenPendingMsg } from './SingleChatMessageActions';
import {
    MESSAGE_DATA,
    MESSAGE_FORWARD_ADD,
    MESSAGE_FORWARD_REMOVE,
    MESSAGE_FORWARD_RESET,
    MESSAGE_INFO_DATA,
    RECENT_RECALL_UPDATE,
    RECENT_REMOVE_MESSAGE_UPDATE,
    RECENT_STATUS_UPDATE,
    UPDATE_MESSAGE_STATUS,
    DELETE_MESSAGE_FOR_ME,
    DELETE_MESSAGE_FOR_EVERYONE,
    MESSAGE_INFO_UPDATE,
    UPDATE_STARRED_MESSAGE_LIST,
    UPDATE_STARRED_MESSAGE_STATUS,
    CLEAR_ALL_CHAT,
    SELECTED_MESSAGE_INFO,
    REACTION_DATA_UPDATE,
    REACTION_DATA_REMOVE,
    UPDATE_MESSAGE_REACTION,
} from './Constants';

const getTypingUserDetails = newChatFrom => {
    const rosterDetail = getDataFromRoster(newChatFrom);
    return rosterDetail ? getContactNameFromRoster(rosterDetail) : getFormatPhoneNumber(newChatFrom.split('@')[0]);
};

const typingTime = {};
export const MessageAction = data => async (dispatch, getState) => {
    const { timestamp = Date.now(), msgType, type, msgId: currentMsgId = null, fromUserId = null } = data;
    if (!msgType || msgType === 'carbonReceiveAcknowledge' || msgType === 'carbonSentAcknowledge') {
        return;
    }

    if (msgType === 'msg_reaction') {
        const {
            reactionMsgId,
            reactionEmojiId,
            action,
            groupId,
            reactionType,
            publisherId,
            nickName,
            senderId,
            reactedUsername,
            publisherJid,
            messageSenderId,
        } = data;
        const chatId = reactionType === 'chat' ? fromUserId : groupId || fromUserId;
        const rawReactorId = publisherId || nickName || senderId || reactedUsername || publisherJid || fromUserId;
        const reactorId = typeof rawReactorId === 'string' ? rawReactorId.split('@')[0] : rawReactorId;
        const normalizedChatId = chatId.includes('@') ? chatId.split('@')[0] : chatId;
        const currentState = getState();
        const history = currentState?.chatConversationHistory?.data?.[normalizedChatId]?.messages || {};
        let reactedMessage = history[reactionMsgId];
        let vCardData = currentState?.vCardData?.data || {};
        // FETCH MISSING MESSAGE FROM SDK
        if (!reactedMessage && action !== 'remove') {
            try {
                let chatIdForFetch = formatUserIdToJid(normalizedChatId, reactionType);
                const apiRes = await SDK.getMessageById(reactionMsgId, chatIdForFetch);
                if (apiRes && apiRes.statusCode === 200) {
                    reactedMessage = apiRes.data;
                }
            } catch (error) {
                logger.error('Failed to fetch reacted message:', error);
            }
        }

        const STATUS_TYPES = [
            'Processing',
            'delivered',
            'carbonDelivered',
            'seen',
            'carbonSeen',
            'acknowledge',
            'carbonSentSeen',
        ];

        // ── SIMPLIFIED REACTION FLOW ──────────────────────────────────────────
        // ADD / UPDATE: compute the new reactions directly on the reacted message
        //   and update the recent chat only when the reaction is relevant to the
        //   current user (they are either the message sender or the reactor).
        // REMOVE: always ask the SDK for the authoritative last message.
        // ─────────────────────────────────────────────────────────────────────

        const buildSdkRecentUpdate = (res, chatId) => ({
            ...res.data,
            msgId: res.data.msgId,
            msgBody: res.data.msgBody
                ? typeof res.data.msgBody === 'string'
                    ? { message: res.data.msgBody }
                    : res.data.msgBody
                : res.data.msgBody,
            msgType: res.data.msgType,
            reactions: transformReactionsGrouped(
                (res.data.reactions || []).map(r => ({
                    emojiId: r.emoji_id || r.emojiId,
                    reactedUsername: r.reaction_from || r.reactedUsername,
                    createdTime: r.message_time || r.timestamp || Date.now(),
                })),
            ),
            filterBy: chatId,
        });

        // A 1-1 reaction carries no message_sender, so its author comes from the message.
        const authorId = messageSenderId || String(reactedMessage?.publisherId || '').split('@')[0];

        if (action === 'add' || action === 'update') {
            // Compute the new reaction list from the reacted message
            const currentReactions = reactedMessage?.reactions || [];
            const newReactions = updateReactionList(currentReactions, reactionEmojiId, reactorId, action);

            // Update the reaction store so other parts of the UI stay in sync
            dispatch({
                type: REACTION_DATA_UPDATE,
                payload: { msgId: reactionMsgId, reactions: newReactions, lastReactionTime: Date.now() * 1000 },
            });

            // FR93: the reaction becomes the latest message for its author and its reactor only.
            const isRelevant = vCardData.userId === authorId || vCardData.userId === reactorId;
            if (isRelevant) {
                let resolvedMsgType = reactedMessage?.msgBody?.message_type || reactedMessage?.msgType || 'text';
                if (STATUS_TYPES.includes(resolvedMsgType)) resolvedMsgType = 'text';

                dispatch(
                    RecentChatUpdateAction({
                        ...(reactedMessage || {}),
                        msgType: resolvedMsgType,
                        MessageType: resolvedMsgType,
                        filterBy: normalizedChatId,
                        fromUserId: normalizedChatId,
                        messageSenderId: authorId,
                        reactedUserName: reactorId,
                        emojiId: reactionEmojiId,
                        reactions: newReactions,
                        // Milliseconds, like every other row of the list: the chat moves to the top.
                        timestamp: Date.now(),
                    }),
                );
            } else {
                // Reaction doesn't involve current user — fetch the real last
                // message from SDK so we don't overwrite the correct state.
                const chatTypeForSDK = reactionType === 'chat' ? 'chat' : 'normal';
                const toUserJidForSDK = formatUserIdToJid(normalizedChatId, reactionType);
                setTimeout(() => {
                    SDK.getRecentChatLastMessage({ chatType: chatTypeForSDK, toUserJid: toUserJidForSDK }).then(res => {
                        if (res?.statusCode === 200 && res.data?.msgId) {
                            dispatch(RecentChatUpdateAction(buildSdkRecentUpdate(res, normalizedChatId)));
                        }
                    });
                }, 300);
            }
        } else if (action === 'remove') {
            // Remove the reaction from the store …
            dispatch({ type: REACTION_DATA_REMOVE, payload: { msgId: reactionMsgId } });

            // … then let the SDK tell us what the true last message is now.
            const chatTypeForSDK = reactionType === 'chat' ? 'chat' : 'normal';
            const toUserJidForSDK = formatUserIdToJid(normalizedChatId, reactionType);
            setTimeout(() => {
                SDK.getRecentChatLastMessage({ chatType: chatTypeForSDK, toUserJid: toUserJidForSDK }).then(res => {
                    if (res?.statusCode === 200 && res.data?.msgId) {
                        const updatedMsg = buildSdkRecentUpdate(res, normalizedChatId);
                        reactedMessage = updatedMsg;
                        dispatch(RecentChatUpdateAction(updatedMsg));
                    }
                });
            }, 500);
        }

        // Update the conversation-message object itself (reactions bubble / tick state)
        let finalMsgType = reactedMessage?.msgBody?.message_type || reactedMessage?.msgType || 'text';
        if (STATUS_TYPES.includes(finalMsgType)) finalMsgType = 'text';

        dispatch(
            updateMessageReaction({
                fromUserId: chatId,
                msgId: reactionMsgId,
                emojiId: reactionEmojiId,
                userId: reactorId,
                action: action,
                msgBody: reactedMessage?.msgBody,
                msgType: finalMsgType,
                messageSenderId: authorId,
                reactedUserName: reactorId,
            }),
        );
        data.msgBody = reactedMessage?.msgBody;
        data.chatType = reactionType === 'chat' ? 'chat' : 'groupchat';
        // Only the author is notified, and not of a reaction they made from another device.
        const isViewerNotified = vCardData.userId === authorId && vCardData.userId !== reactorId;
        const sendNotificationFlag =
            isViewerNotified &&
            action === 'add' &&
            !isActiveConversationUserOrGroup(fromUserId) &&
            !getMutedChats().includes(fromUserId);
        if (sendNotificationFlag) {
            const { title, image, message } = await getPushNotificationData(data);
            sendNotification(title, image, message, fromUserId);
        }
        return;
    }

    const getcurrentState = getState();

    // When message is seen, delete from pending seen message list
    if (
        msgType === MSG_SENT_SEEN_STATUS_CARBON ||
        (msgType === MSG_SEEN_ACKNOWLEDGE_STATUS && type === MSG_SEEN_STATUS)
    ) {
        const pendingMessagesData = getcurrentState?.chatSeenPendingMsgData?.data || {};
        const hasPendingMessage = Object.keys(pendingMessagesData).some(key => key.endsWith(`_${currentMsgId}`));
        if (hasPendingMessage) dispatch(deleteChatSeenPendingMsg(currentMsgId));
    }

    if (msgType === 'composing' || msgType === 'carbonComposing' || msgType === 'carbonGone' || msgType === 'gone') {
        if (msgType === 'composing' || msgType === 'carbonComposing') {
            const { groupId } = data;
            let displayName;
            if (groupId) {
                displayName = getTypingUserDetails(fromUserId);
            }
            data.displayName = displayName;
            dispatch(TypingDataAction(data));
            clearTimeout(typingTime[data.fromUserId]);
            typingTime[data.fromUserId] = setTimeout(() => {
                dispatch(TypingDataRemove(data));
            }, TYPE_DELAY_TIME + 1000);
        } else {
            dispatch(TypingDataRemove(data));
        }
    }

    if (
        msgType === 'recallMessage' ||
        msgType === 'carbonRecallMessage' ||
        msgType === 'carbonSentRecall' ||
        msgType === 'carbonReceiveRecall' ||
        (msgType === 'acknowledge' && type === 'recall')
    ) {
        dispatch({
            type: RECENT_RECALL_UPDATE,
            payload: {
                id: uuidv4(),
                data,
            },
        });
        dispatch({
            type: DELETE_MESSAGE_FOR_EVERYONE,
            payload: {
                id: uuidv4(),
                data,
            },
        });
        messagesIdb.updateDeletedMessagesInIdb(DELETE_MESSAGE_FOR_EVERYONE, data);
        dispatch({
            type: UPDATE_STARRED_MESSAGE_LIST,
            payload: {
                id: uuidv4(),
                data: {
                    msgId: data.msgId,
                    favouriteStatus: 0,
                },
            },
        });
        handleTempArchivedChats(data.fromUserId, data.chatType);
    }

    if (
        msgType === 'carbonMessageClear' ||
        msgType === 'messageClear' ||
        msgType === 'clear_message' ||
        msgType === MSG_DELETE_STATUS ||
        msgType === MSG_DELETE_STATUS_CARBON
    ) {
        dispatch({
            type: RECENT_REMOVE_MESSAGE_UPDATE,
            payload: {
                id: uuidv4(),
                data,
            },
        });
        dispatch({
            type: DELETE_MESSAGE_FOR_ME,
            payload: {
                id: uuidv4(),
                data,
            },
        });
        messagesIdb.updateDeletedMessagesInIdb(DELETE_MESSAGE_FOR_ME, data);

        handleTempArchivedChats(data.fromUserId, data.chatType);

        // Removing All Starred Messages
        if (msgType === MSG_DELETE_STATUS || msgType === MSG_DELETE_STATUS_CARBON) {
            for (const msgId in data.msgIds) {
                const obj = {
                    favouriteBy: '',
                    favouriteStatus: 0,
                    msgId,
                };
                messagesIdb.updateMessage(obj);
                dispatch({
                    type: UPDATE_STARRED_MESSAGE_LIST,
                    payload: {
                        id: uuidv4(),
                        data: {
                            msgId: data.msgIds[msgId],
                            favouriteStatus: 0,
                        },
                    },
                });
            }
        }

        if ((msgType === MSG_DELETE_STATUS || msgType === MSG_DELETE_STATUS_CARBON) && data.lastMsgId) {
            SDK.getMessageById(data.lastMsgId, formatUserIdToJid(data.fromUserId, data.chatType));
        }
    }
    if (msgType === 'carbonDelivered' || msgType === 'delivered' || msgType === 'carbonSeen' || msgType === 'seen') {
        const msgStatus =
            msgType === MSG_SEEN_STATUS_CARBON
                ? MSG_SEEN_STATUS_ID
                : msgType === MSG_DELIVERED_STATUS_CARBON || msgType === MSG_DELIVERED_STATUS
                  ? MSG_DELIVERED_STATUS_ID
                  : MSG_SEEN_STATUS_ID;
        const fullMessage = getMessageFromHistoryById(data.fromUserId, data.msgId);
        const chatType = data.chatType || fullMessage.chatType;
        const obj = {
            ...fullMessage,
            msgStatus,
            msgId: data.msgId,
            userJid: fullMessage.userJid || formatUserIdToJid(data.fromUserId, chatType),
            ...(fullMessage.timestamp !== undefined && { timestamp: fullMessage.timestamp }),
        };
        messagesIdb.upsertMessage(obj);

        // const { msgType, ...rest } = data;
        dispatch({
            type: UPDATE_MESSAGE_STATUS,
            payload: {
                id: uuidv4(),
                data,
            },
        });
        dispatch({
            type: MESSAGE_INFO_UPDATE,
            payload: {
                id: uuidv4(),
                activeUserId: data.publisherId,
                time: timestamp,
                editedTimeUpdated: data.timestamp,
                messageStatus: msgStatus,
            },
        });
        dispatch({
            type: UPDATE_STARRED_MESSAGE_STATUS,
            payload: {
                id: uuidv4(),
                data,
            },
        });
    }

    if (msgType === 'acknowledge' && type === 'acknowledge') {
        const fullMessage = getMessageFromHistoryById(data.fromUserId, data.msgId);
        const chatType = data.chatType || fullMessage.chatType;
        const obj = {
            ...fullMessage,
            msgId: data.msgId,
            msgStatus: 0,
            userJid: fullMessage.userJid || data.fromUserJid || formatUserIdToJid(data.fromUserId, chatType),
            timestamp: data.timestamp,
        };
        messagesIdb.upsertMessage(obj);

        dispatch({
            type: UPDATE_MESSAGE_STATUS,
            payload: {
                id: uuidv4(),
                data,
            },
        });
    }

    if (msgType === 'carbonClearAllChat') {
        dispatch({
            type: CLEAR_ALL_CHAT,
            payload: {
                id: uuidv4(),
                data,
            },
        });
    }

    dispatch({
        type: MESSAGE_DATA,
        payload: {
            id: uuidv4(),
            data,
        },
    });

    if (
        msgType === 'sentMessage' ||
        msgType === 'carbonSentMessage' ||
        msgType === 'carbonReceiveMessage' ||
        msgType === 'receiveMessage' ||
        msgType === GROUP_CHAT_PROFILE_UPDATED_NOTIFY
    ) {
        updateMessageUnreadCount(data, getcurrentState);
        updateConversationMessage(data, getcurrentState);
        handleArchiveActions(data);
        updateRecentChatMessage(data, getcurrentState);
        return;
    }

    if (msgType === 'carbonSentSeen') {
        updateMessageUnreadCount(data, getcurrentState);
    }
};

export const MessageActionEdited = data => {
    return {
        type: 'MESSAGE_EDIT',
        payload: {
            id: uuidv4(),
            data,
        },
    };
};

export const messageInfoAction = (data, _forceUpdate) => {
    const { statusUpdate = true, msgId } = data;
    return (dispatch, getState) => {
        const getcurrentState = getState();
        if (statusUpdate) {
            const messageObject = getcurrentState?.recentChatData?.data.find(ele => ele.msgId === msgId);
            if (messageObject) {
                dispatch({
                    type: RECENT_STATUS_UPDATE,
                    payload: {
                        id: uuidv4(),
                        data,
                    },
                });
            }
        }

        dispatch({
            type: MESSAGE_INFO_DATA,
            payload: {
                id: uuidv4(),
                data,
            },
        });
    };
};

export const messageForwardAdd = data => {
    return {
        type: MESSAGE_FORWARD_ADD,
        payload: {
            id: uuidv4(),
            data,
        },
    };
};

export const messageForwardRemove = data => {
    return {
        type: MESSAGE_FORWARD_REMOVE,
        payload: {
            id: uuidv4(),
            data,
        },
    };
};

export const messageForwardReset = () => {
    return {
        type: MESSAGE_FORWARD_RESET,
        payload: {},
    };
};

export const selectedMessageInfo = data => {
    return dispatch => {
        dispatch({
            type: SELECTED_MESSAGE_INFO,
            payload: data,
        });
    };
};

export const updateMessageReaction = data => {
    return {
        type: UPDATE_MESSAGE_REACTION,
        payload: {
            id: uuidv4(),
            data,
        },
    };
};

export const handleLocalReactionUpdate = data => async (dispatch, getState) => {
    const {
        fromUserId,
        msgId,
        emojiId,
        userId,
        action,
        msgBody,
        msgType,
        messageType,
        timestamp,
        lastMessage,
        chatType,
    } = data;
    const currentState = getState();
    const normalizedChatId = fromUserId.includes('@') ? fromUserId.split('@')[0] : fromUserId;

    const STATUS_TYPES = [
        'Processing',
        'delivered',
        'carbonDelivered',
        'seen',
        'carbonSeen',
        'acknowledge',
        'carbonSentSeen',
    ];

    // ── SIMPLIFIED REACTION FLOW ──────────────────────────────────────────────
    // ADD / UPDATE: compute new reactions directly on the reacted message and
    //   update the recent chat. The local user is always the reactor so the
    //   reaction is always relevant.
    // REMOVE: ask the SDK for the authoritative last message.
    // ─────────────────────────────────────────────────────────────────────────

    // Resolve the reacted message from history or the hint passed by the caller
    let reactedMessage =
        currentState?.chatConversationHistory?.data?.[normalizedChatId]?.messages?.[msgId] || lastMessage;
    // If still missing, fetch it from the SDK so we have the msgBody for the preview
    if (!reactedMessage && msgId && action !== 'remove') {
        try {
            const chatIdForFetch = formatUserIdToJid(normalizedChatId, chatType);
            const apiRes = await SDK.getMessageById(msgId, chatIdForFetch);
            if (apiRes && apiRes.statusCode === 200) {
                reactedMessage = apiRes.data;
            }
        } catch (error) {
            logger.error('Failed to fetch reacted message on sender side:', error);
        }
    }

    const reactionType = chatType === 'groupchat' ? 'groupchat' : 'chat';
    const messageSenderId =
        typeof (
            reactedMessage?.publisherId ||
            reactedMessage?.publisher ||
            reactedMessage?.senderId ||
            reactedMessage?.fromUserId
        ) === 'string'
            ? (
                  reactedMessage?.publisherId ||
                  reactedMessage?.publisher ||
                  reactedMessage?.senderId ||
                  reactedMessage?.fromUserId
              ).split('@')[0]
            : reactedMessage?.publisherId ||
              reactedMessage?.publisher ||
              reactedMessage?.senderId ||
              reactedMessage?.fromUserId;

    const buildSdkRecentUpdate = (res, cId) => ({
        ...res.data,
        msgId: res.data.msgId,
        msgBody: res.data.msgBody
            ? typeof res.data.msgBody === 'string'
                ? { message: res.data.msgBody }
                : res.data.msgBody
            : res.data.msgBody,
        msgType: res.data.msgType,
        reactions: transformReactionsGrouped(
            (res.data.reactions || []).map(r => ({
                emojiId: r.emoji_id || r.emojiId,
                reactedUsername: r.reaction_from || r.reactedUsername,
                createdTime: r.message_time || r.timestamp || Date.now(),
            })),
        ),
        filterBy: cId,
    });

    if (action === 'add' || action === 'update') {
        const currentReactions = reactedMessage?.reactions || [];
        const newReactions = updateReactionList(currentReactions, emojiId, userId, action);

        // Update the reaction store
        dispatch({
            type: REACTION_DATA_UPDATE,
            payload: { msgId, reactions: newReactions, lastReactionTime: Date.now() * 1000 },
        });

        // The local user is always the reactor, so the reaction is their latest message (FR93).
        let resolvedMsgType = reactedMessage?.msgBody?.message_type || reactedMessage?.msgType || 'text';
        if (STATUS_TYPES.includes(resolvedMsgType)) resolvedMsgType = 'text';

        dispatch(
            RecentChatUpdateAction({
                ...(reactedMessage || {}),
                msgType: resolvedMsgType,
                MessageType: resolvedMsgType,
                filterBy: normalizedChatId,
                fromUserId: normalizedChatId,
                messageSenderId: messageSenderId,
                reactedUserName: typeof userId === 'string' ? userId.split('@')[0] : userId,
                emojiId,
                reactions: newReactions,
                // Milliseconds, like every other row of the list: the chat moves to the top.
                timestamp: Date.now(),
            }),
        );
    } else if (action === 'remove') {
        // Remove reaction from store …
        dispatch({ type: REACTION_DATA_REMOVE, payload: { msgId } });

        // … then ask the SDK for the true last message.
        const chatTypeForSDK = chatType === 'chat' ? 'chat' : 'normal';
        const toUserJidForSDK = formatUserIdToJid(normalizedChatId, chatType);
        setTimeout(() => {
            SDK.getRecentChatLastMessage({ chatType: chatTypeForSDK, toUserJid: toUserJidForSDK }).then(res => {
                if (res?.statusCode === 200 && res.data?.msgId) {
                    dispatch(RecentChatUpdateAction(buildSdkRecentUpdate(res, normalizedChatId)));
                }
            });
        }, 500);
    }

    // Update the conversation-message object itself (reactions bubble / tick state)
    let finalMsgType = reactedMessage?.msgBody?.message_type || reactedMessage?.msgType || 'text';
    if (STATUS_TYPES.includes(finalMsgType)) finalMsgType = 'text';

    dispatch(
        updateMessageReaction({
            fromUserId: fromUserId,
            msgId: msgId,
            emojiId: emojiId,
            userId: userId,
            action: action,
            msgBody: reactedMessage?.msgBody,
            msgType: finalMsgType,
            messageSenderId: messageSenderId,
            reactedUserName: typeof userId === 'string' ? userId.split('@')[0] : userId,
        }),
    );
};
